import React, { createContext, useContext, useState, useEffect } from 'react';
import { 
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut,
  sendPasswordResetEmail,
  onAuthStateChanged,
  User as FirebaseUser,
  getIdTokenResult
} from 'firebase/auth';
import { 
  doc, 
  getDoc, 
  setDoc, 
  updateDoc,
  Timestamp 
} from 'firebase/firestore';
import { auth, db, FIREBASE_PROJECT_ID } from '../config/firebase';
import { User } from '../types/user.types';
import { toast } from 'react-hot-toast';

interface AuthContextType {
  currentUser: FirebaseUser | null;
  userProfile: User | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<void>;
  register: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
  refreshToken: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [currentUser, setCurrentUser] = useState<FirebaseUser | null>(null);
  const [userProfile, setUserProfile] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  const login = async (email: string, password: string) => {
    await signInWithEmailAndPassword(auth, email, password);
    await refreshToken();  // Force token refresh on login
  };

  const register = async (email: string, password: string) => {
    await createUserWithEmailAndPassword(auth, email, password);
  };

  const logout = async () => {
    setUserProfile(null);
    await signOut(auth);
  };

  const resetPassword = async (email: string) => {
    await sendPasswordResetEmail(auth, email);
  };

  const refreshToken = async () => {
    if (currentUser) {
      await currentUser.getIdToken(true);
      const tokenResult = await currentUser.getIdTokenResult();
      //console.log('Token refreshed. Claims:', tokenResult.claims);
    }
  };

  useEffect(() => {
    //console.log('Setting up auth state listener...');
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
     // console.log('Auth state changed:', user ? `User ${user.uid}` : 'No user');
      setCurrentUser(user);
      
      if (user) {
        try {
          await refreshToken();  // Force token refresh on auth change
          const userDoc = await getDoc(doc(db, 'users', user.uid));
          if (userDoc.exists()) {
            const userData = userDoc.data();
            
            const fullUserData = {
              ...userData,
              uid: user.uid
            } as User;
            
            if (!fullUserData.projects || fullUserData.projects.length === 0) {
              //console.log('Updating user with project ID...');
              await updateDoc(doc(db, 'users', user.uid), {
                projects: [FIREBASE_PROJECT_ID],
                updatedAt: Timestamp.now()
              });
              fullUserData.projects = [FIREBASE_PROJECT_ID];
            }
            
            setUserProfile(fullUserData);
            //console.log('User profile loaded:', fullUserData.role);
            
            const tokenResult = await user.getIdTokenResult();
            
            if (tokenResult.claims.role !== fullUserData.role) {
              //console.log('Token role mismatch. Token:', tokenResult.claims.role, 'Firestore:', fullUserData.role);
            }
          } else {
           // console.log('No user profile found, creating default...');
            const defaultProfile = {
              uid: user.uid,
              email: user.email || '',
              name: user.displayName || 'User',
              role: 'field' as const,
              organizationId: '', // Will need to be set by admin
              organizationName: '',
              projects: [FIREBASE_PROJECT_ID],
              isActive: true,
              createdAt: Timestamp.now(),
              updatedAt: Timestamp.now()
            };
            
            await setDoc(doc(db, 'users', user.uid), defaultProfile);
            const newUserDoc = await getDoc(doc(db, 'users', user.uid));
            if (newUserDoc.exists()) {
              setUserProfile({ ...newUserDoc.data(), uid: user.uid } as User);
            }
          }
        } catch (error) {
          console.error('Error loading user profile:', error);
        }
      } else {
        setUserProfile(null);
      }
      
      setLoading(false);
    });

    return () => {
     // console.log('Cleaning up auth state listener...');
      unsubscribe();
    };
  }, []);

  const value = {
    currentUser,
    userProfile,
    login,
    register,
    logout,
    resetPassword,
    refreshToken,
    loading
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};